import { AttributionCategory } from '@frontend/pricing-search';
import moment from 'moment';

export const CARDS_TITLES = {
  LockExtensionAdjustment: 'Lock Extensions',
  ReLockFeeAdjustment: 'Re-Lock Fees',
  CustomPriceAdjustment: 'Custom Adjustments',
  CorporatePriceConcession: 'Corporate Price Concessions',
  BranchPriceConcession: 'Branch Price Concessions',
};

export const AdjustmentType = {
  LockExtensionAdjustment: 'LockExtensionAdjustment',
  ReLockFeeAdjustment: 'ReLockFeeAdjustment',
  CustomPriceAdjustment: 'CustomPriceAdjustment',
  BranchPriceConcession: 'BranchPriceConcession',
  CorporatePriceConcession: 'CorporatePriceConcession',
};

export const CARDS_TEMPLATES = {
  adjustments: {
    description: '',
    price: '',
    persist: true,
  },
  concessions: {
    title: '',
    approvalDate: moment().format('L'),
    price: '',
    approvedBy: '',
    approvedReason: '',
    persist: true,
  },
};

export const CARDS_LIMIT = {
  adjustments: 10,
  concessions: 5,
};

const errorMessages = {
  required: 'Required',
  atleastOne: 'One of Rate or Price is required',
  customAdjustmentRange: 'Between -25 and 25',
  priceRange: 'Between -9.999 and 9.999',
  noDashAllowed: 'Dashes are not allowed',
};

const adjustmentsValidator = {
  fieldName: 'adjustments',
  path: 'adjustments',
  customValidator: (
    _,
    rows: {
      price: string;
      description: string;
      rate: string;
      adjustmentType: string;
      adjustmentReasonGuid: string | null;
      paidByGuid: string | null;
    }[] = [],
  ) => {
    const validation: {
      hasError: boolean;
      messages: { price: string; description: string; rate: string }[];
    } = { hasError: false, messages: [] };

    rows.forEach((row, index) => {
      const rowError = {
        price: '',
        description: '',
        rate: '',
        adjustmentReasonGuid: '',
        paidByGuid: '',
      };
      const {
        price,
        description,
        rate,
        adjustmentType,
        adjustmentReasonGuid,
        paidByGuid,
      } = row;
      const priceValue = parseFloat(price);
      const rateValue = parseFloat(rate);
      const isCustomAdjustment = adjustmentType === 'CustomPriceAdjustment';
      // if there is no value in name
      if (!(description || '').trim()) {
        validation.hasError = true;
        rowError.description = errorMessages.required;
      }
      // if there is no adjustment reason guid. If it comes in null, that means there are no options to select from and to not error
      if (adjustmentReasonGuid === undefined || adjustmentReasonGuid === '') {
        validation.hasError = true;
        rowError.adjustmentReasonGuid = errorMessages.required;
      }
      // if there is no paid by guid. If it comes in null, that means there are no options to select from and to not error
      if (paidByGuid === undefined || paidByGuid === '') {
        validation.hasError = true;
        rowError.paidByGuid = errorMessages.required;
      }
      // Disallow dashes to better stabalize category splits
      if (description?.includes('-')) {
        validation.hasError = true;
        rowError.description = errorMessages.noDashAllowed;
      }
      // validation for custom adjustment
      if (isCustomAdjustment) {
        if (Number.isNaN(rateValue) && Number.isNaN(priceValue)) {
          rowError.rate = errorMessages.atleastOne;
          rowError.price = errorMessages.atleastOne;
        }
        if (priceValue < -25 || priceValue > 25) {
          rowError.price = errorMessages.customAdjustmentRange;
        }
        if (rateValue < -25 || rateValue > 25) {
          rowError.rate = errorMessages.customAdjustmentRange;
        }
      }
      // validation for Re-Lock Fees, Corporate Price Concessions, Branch Price Concessions
      if (!isCustomAdjustment) {
        if (Number.isNaN(priceValue)) {
          rowError.price = errorMessages.required;
        } else if (priceValue < -9.999 || priceValue > 9.999) {
          rowError.price = errorMessages.priceRange;
        } else if (priceValue === 0) {
          rowError.price = errorMessages.required;
        }
      }

      if (rowError.price === '' && rowError.rate === '') {
        rowError.rate = '';
        rowError.price = '';
      } else validation.hasError = true;

      validation.messages[index] = rowError;
    });

    return validation;
  },
};

const concessionsValidator = {
  fieldName: 'concessions',
  path: 'concessions',
  customValidator: (
    _,
    rows: {
      price: string;
      approvalDate: string;
      paidByGuid: string;
      adjustmentReasonGuid: string;
      approvedReason: string;
    }[] = [],
  ) => {
    const validation: {
      hasError: boolean;
      messages: {
        price: string;
        approvalDate: string;
        paidByGuid: string;
        adjustmentReasonGuid: string;
        approvedReason: string;
      }[];
    } = { hasError: false, messages: [] };

    rows.forEach((row, index) => {
      const rowError = {
        price: '',
        approvalDate: '',
        paidByGuid: '',
        adjustmentReasonGuid: '',
        approvedReason: '',
      };
      const {
        price,
        approvalDate,
        paidByGuid,
        adjustmentReasonGuid,
        approvedReason,
      } = row;
      const priceValue = parseFloat(price);

      if (Number.isNaN(priceValue)) {
        validation.hasError = true;
        rowError.price = errorMessages.required;
      }
      if (priceValue < -9.999 || priceValue > 9.999) {
        validation.hasError = true;
        rowError.price = errorMessages.priceRange;
      }
      // if there is no adjustment reason guid. If it comes in null, that means there are no options to select from and to not error
      if (adjustmentReasonGuid === undefined || adjustmentReasonGuid === '') {
        validation.hasError = true;
        rowError.adjustmentReasonGuid = errorMessages.required;
      }
      // if there is no paid by guid. If it comes in null, that means there are no options to select from and to not error
      if (paidByGuid === undefined || paidByGuid === '') {
        validation.hasError = true;
        rowError.paidByGuid = errorMessages.required;
      }
      // Disallow dashes to better stabalize category splits
      if (approvedReason?.includes('-')) {
        validation.hasError = true;
        rowError.approvedReason = errorMessages.noDashAllowed;
      }

      if (priceValue === 0) {
        validation.hasError = true;
        rowError.price = errorMessages.required;
      }
      if (!(approvalDate || '').trim()) {
        validation.hasError = true;
        rowError.approvalDate = errorMessages.required;
      }
      validation.messages[index] = rowError;
    });

    return validation;
  },
};

export const validators = {
  adjustments: adjustmentsValidator,
  concessions: concessionsValidator,
};

export const mockCategoryData: AttributionCategory[] = [
  {
    guid: '1',
    attributionCategoryTypeID: 1,
    name: 'Category 1',
    abbreviation: 'CAT1',
    parent: [1],
    active: 'Yes',
  },
  {
    guid: '2',
    attributionCategoryTypeID: 2,
    name: 'Category 2',
    abbreviation: 'CAT2',
    parent: [1, 2],
    active: 'No',
  },
  {
    guid: '3',
    attributionCategoryTypeID: 1,
    name: 'Category 3',
    abbreviation: 'CAT3',
    parent: [1],
    active: 'No',
  },
  {
    guid: '4',
    attributionCategoryTypeID: 2,
    name: 'Category 4',
    abbreviation: 'CAT4',
    parent: [1, 2],
    active: 'Yes',
  },
  {
    guid: '5',
    attributionCategoryTypeID: 2,
    name: 'Category 5',
    abbreviation: 'CAT5',
    parent: [1, 2],
    active: 'Yes',
  },
  {
    guid: '6',
    attributionCategoryTypeID: 2,
    name: 'Category 6',
    abbreviation: 'CAT6',
    parent: [1, 2],
    active: 'Yes',
  },
  {
    guid: '7',
    attributionCategoryTypeID: 2,
    name: 'Category 7',
    abbreviation: 'CAT7',
    parent: [2],
    active: 'No',
  },
  {
    guid: '8',
    attributionCategoryTypeID: 2,
    name: 'Category 8',
    abbreviation: 'CAT8',
    parent: [2],
    active: 'Yes',
  },
  {
    guid: '9',
    attributionCategoryTypeID: 1,
    name: 'Category 9',
    abbreviation: 'CAT9',
    parent: [1, 2],
    active: 'No',
  },
  {
    guid: '10',
    attributionCategoryTypeID: 1,
    name: 'Category 10',
    abbreviation: 'CAT10',
    parent: [2, 3],
    active: 'Yes',
  },
];
